import { ValuationRegionTypeSchema } from './geo';
import { MonetaryValueSchema, SquareMetreMoneyPerAreaValueSchema, MoneyPerUnitSchema } from './money';
import { PercentageSchema, AreaValueSchema } from './measurements';
import { z } from 'zod';
import { UUIDSchema, ISODateSchema } from './common';
export const RegionTypeSchema = ValuationRegionTypeSchema;
const MarketDataRegionShape = {
    uuid: UUIDSchema,
    geom: z.string(),
    name: z.string(),
    name_prefix: z.string().optional(),
    region_ref: z.string(),
    region_type: RegionTypeSchema,
    tags: z.array(z.string()),
};
export const MarketDataRegionSchema = z.object(MarketDataRegionShape);
const MarketDataRegionListShape = { regions: z.array(MarketDataRegionSchema) };
export const MarketDataRegionListSchema = z.object(MarketDataRegionListShape);
export const MarketDataPublisherTypeSchema = z.enum([
    'expert_committee',
    'public_administration',
    'research_institute',
    'bank',
    'broker',
    'municipality',
    'other',
]);
const NewMarketDataPublisherShape = { name: z.string(), publisher_type: MarketDataPublisherTypeSchema, webpage: z.string().optional() };
export const NewMarketDataPublisherSchema = z.object(NewMarketDataPublisherShape);
const MarketDataPublisherShape = {
    ...NewMarketDataPublisherShape,
    uuid: UUIDSchema,
};
export const MarketDataPublisherSchema = z.object(MarketDataPublisherShape);
const MarketDataPublisherListShape = {
    publishers: z.array(MarketDataPublisherSchema),
};
export const MarketDataPublisherListSchema = z.object(MarketDataPublisherListShape);
export const NewMarketDataOriginLinkSchema = z.object({
    name: z.string(),
    url: z.string(),
    is_public: z.boolean(),
    copy_right: z.string().optional(),
    description: z.string().optional(),
});
const MarketDataOriginLinkShape = {
    ...NewMarketDataOriginLinkSchema.shape,
    uuid: UUIDSchema,
};
export const MarketDataOriginLinkSchema = z.object(MarketDataOriginLinkShape);
const MarketDataOriginLinkListShape = {
    links: z.array(MarketDataOriginLinkSchema),
};
export const MarketDataOriginLinkListSchema = z.object(MarketDataOriginLinkListShape);
export const NewMarketDataOriginInformalSourceSchema = z.object({
    name: z.string(),
    is_public: z.boolean(),
    copy_right: z.string().optional(),
    description: z.string().optional(),
});
const MarketDataOriginInformalSourceShape = {
    ...NewMarketDataOriginInformalSourceSchema.shape,
    uuid: UUIDSchema,
};
export const MarketDataOriginInformalSourceSchema = z.object(MarketDataOriginInformalSourceShape);
const MarketDataOriginInformalSourceListShape = {
    informal_sources: z.array(MarketDataOriginInformalSourceSchema),
};
export const MarketDataOriginInformalSourceListSchema = z.object(MarketDataOriginInformalSourceListShape);
export const UpdateMarketDataOriginDocumentSchema = z.object({
    name: z.string(),
    tags: z.array(z.string()).optional(),
    hide_file: z.boolean(),
    description: z.string().optional(),
    publication_date: z.string().optional(),
    is_public: z.boolean(),
    copy_right: z.string().optional(),
});
const MarketDataOriginDocumentShape = {
    ...UpdateMarketDataOriginDocumentSchema.shape,
    uuid: z.string(),
    url: z.string(),
    content_type: z.string(),
    created_on: z.string().optional(),
};
export const MarketDataOriginDocumentSchema = z.object(MarketDataOriginDocumentShape);
const MarketDataOriginDocumentListShape = {
    documents: z.array(MarketDataOriginDocumentSchema),
};
export const MarketDataOriginDocumentListSchema = z.object(MarketDataOriginDocumentListShape);
const MarketDataMoneyPerUnitValueShape = {
    ...MoneyPerUnitSchema.shape,
    area_unit: z.literal('EH'),
};
const MarketDataMoneyPerUnitValueSchema = z.object(MarketDataMoneyPerUnitValueShape);
export const MarketDataUsageTypeSchema = z.enum([
    'retail',
    'supermarket',
    'shopping_street',
    'shopping_center',
    'retail_warehouse',
    'other_retail',
    'office',
    'life_science_lab',
    'office_business_park',
    'medical_facilities',
    'standard_office',
    'industry',
    'logistics',
    'manufacturing',
    'production',
    'flex_spaces',
    'other_industry',
    'residential',
    'multi_family_housing',
    'single_family_housing',
    'student_housing',
    'other_residential',
    'hotel',
    'full_service',
    'limited_service',
    'other_accommodation',
    'senior_living_and_care',
    'nursing_homes',
    'senior_homes',
    'healthcare',
    'other_healthcare',
    'data_center',
    'multi_client_data_center',
    'single_client_enterprise_data_center',
    'other_data_center',
    'land',
    'development_land',
    'agricultural_land',
    'forestry',
    'other_land',
    'other',
    'education',
    'leisure',
    'cinema',
    'theater',
    'raw_material_extraction',
    'parking',
    'self_storage',
    'other_subsectors',
]);
const NewMarketDataOriginShape = {
    document_uuid: UUIDSchema.optional(),
    link_uuid: UUIDSchema.optional(),
    informal_source_uuid: UUIDSchema.optional(),
    num_relevant_records: z.number().optional(),
    num_total_records: z.number().optional(),
};
export const NewMarketDataOriginSchema = z.object(NewMarketDataOriginShape);
const MarketDataOriginPart1Shape = {
    link: MarketDataOriginLinkSchema.optional(),
    document: MarketDataOriginDocumentSchema.optional(),
    informal_source: MarketDataOriginInformalSourceSchema.optional(),
};
const MarketDataOriginPart1Schema = z.object(MarketDataOriginPart1Shape);
export const MarketDataOriginSchema = z.intersection(NewMarketDataOriginSchema, MarketDataOriginPart1Schema);
const MarketDataPointValueValueSchema = z.union([
    MonetaryValueSchema,
    SquareMetreMoneyPerAreaValueSchema,
    MarketDataMoneyPerUnitValueSchema,
    PercentageSchema,
    AreaValueSchema,
    z.number(),
]);
const MarketDataPointValueShape = {
    value: MarketDataPointValueValueSchema,
    label: z.string(),
};
export const MarketDataPointValueSchema = z.object(MarketDataPointValueShape);
const MarketDataDateRangeShape = {
    date_from: ISODateSchema,
    date_to: ISODateSchema,
};
export const MarketDataDateRangeSchema = z.object(MarketDataDateRangeShape);
const TimeSeriesMarketDataPointShape = {
    uuid: UUIDSchema.optional(),
    values: z.array(MarketDataPointValueSchema),
    date_range: MarketDataDateRangeSchema,
    origin: MarketDataOriginSchema,
};
export const TimeSeriesMarketDataPointSchema = z.object(TimeSeriesMarketDataPointShape);
const MarketDataPointMetadataShape = {
    uuid: UUIDSchema,
    publisher_uuid: UUIDSchema,
    publisher: MarketDataPublisherSchema.optional(),
    usage_types: z.array(MarketDataUsageTypeSchema),
    region_uuids: z.array(UUIDSchema),
    regions: z.array(MarketDataRegionSchema).optional(),
    foot_note: z.string().optional(),
    short_description: z.string().optional(),
    full_description: z.string().optional(),
    alt_region_name: z.string().optional(),
    warning: z.string().optional(),
};
const MarketDataPointMetadataSchema = z.object(MarketDataPointMetadataShape);
export const MonetaryValueGroupTypeSchema = z.enum(['market_value', 'transaction_volume']);
const MonetaryMarketDataClassificationShape = {
    value_type: z.literal('monetary_value'),
    group_type: MonetaryValueGroupTypeSchema,
};
const MonetaryMarketDataClassificationSchema = z.object(MonetaryMarketDataClassificationShape);
export const MoneyPerAreaValueGroupTypeSchema = z.enum(['market_area_value', 'land_area_value', 'monthly_rent', 'yearly_rent']);
const MoneyPerAreaMarketDataClassificationShape = {
    value_type: z.literal('money_per_area_value'),
    group_type: MoneyPerAreaValueGroupTypeSchema,
};
const MoneyPerAreaMarketDataClassificationSchema = z.object(MoneyPerAreaMarketDataClassificationShape);
export const AreaValueGroupTypeSchema = z.enum([
    'empty_area',
    'area_in_construction',
    'area_offer',
    'area_stock',
    'area_turnover',
    'rental_turnover',
]);
const AreaMarketDataClassificationShape = {
    value_type: z.literal('area_value'),
    group_type: AreaValueGroupTypeSchema,
};
const AreaMarketDataClassificationSchema = z.object(AreaMarketDataClassificationShape);
export const PercentageGroupTypeSchema = z.enum(['discount_rate', 'vacancy_rate', 'yield']);
const PercentageMarketDataClassificationShape = {
    value_type: z.literal('percentage_value'),
    group_type: PercentageGroupTypeSchema,
};
const PercentageMarketDataClassificationSchema = z.object(PercentageMarketDataClassificationShape);
export const IntegerGroupTypeSchema = z.enum(['multiplier', 'num_stays', 'num_completions']);
const IntegerMarketDataClassificationShape = {
    value_type: z.literal('integer_value'),
    group_type: IntegerGroupTypeSchema,
};
const IntegerMarketDataClassificationSchema = z.object(IntegerMarketDataClassificationShape);
export const MoneyPerUnitGroupTypeSchema = z.enum([
    'value_per_unit',
    'yearly_rent_per_unit',
    'monthly_rent_per_unit',
    'daily_rent_per_unit',
]);
const MoneyPerUnitMarketDataClassificationShape = {
    value_type: z.literal('money_per_unit_value'),
    group_type: MoneyPerUnitGroupTypeSchema,
};
const MoneyPerUnitMarketDataClassificationSchema = z.object(MoneyPerUnitMarketDataClassificationShape);
const MarketDataClassificationSchema = z.union([
    MonetaryMarketDataClassificationSchema,
    MoneyPerAreaMarketDataClassificationSchema,
    AreaMarketDataClassificationSchema,
    PercentageMarketDataClassificationSchema,
    IntegerMarketDataClassificationSchema,
    MoneyPerUnitMarketDataClassificationSchema,
]);
const MarketDataPointWithMetadataSchema = z.intersection(TimeSeriesMarketDataPointSchema, MarketDataPointMetadataSchema);
export const MarketDataPointSchema = z.intersection(MarketDataPointWithMetadataSchema, MarketDataClassificationSchema);
export const MarketDataDateFormatSchema = z.enum(['day', 'month', 'quarter', 'half_year', 'year', 'two_year', 'other']);
const NewMarketDataTimeSeriesFieldsShape = {
    points: z.array(TimeSeriesMarketDataPointSchema),
    date_format: MarketDataDateFormatSchema,
    publisher_uuid: UUIDSchema,
    usage_types: z.array(MarketDataUsageTypeSchema),
    region_uuids: z.array(UUIDSchema),
    foot_note: z.string().optional(),
    short_description: z.string().optional(),
    full_description: z.string().optional(),
    alt_region_name: z.string().optional(),
    warning: z.string().optional(),
};
const NewMarketDataTimeSeriesFieldsSchema = z.object(NewMarketDataTimeSeriesFieldsShape);
export const NewMarketDataTimeSeriesSchema = z.intersection(NewMarketDataTimeSeriesFieldsSchema, MarketDataClassificationSchema);
export const MarketDataPointTypeSchema = z.enum([
    'monetary_value',
    'money_per_area_value',
    'area_value',
    'percentage_value',
    'integer_value',
    'money_per_unit_value',
]);
export const MarketDataGroupTypeSchema = z.union([
    MonetaryValueGroupTypeSchema,
    MoneyPerAreaValueGroupTypeSchema,
    AreaValueGroupTypeSchema,
    PercentageGroupTypeSchema,
    IntegerGroupTypeSchema,
    MoneyPerUnitGroupTypeSchema,
]);
const MarketDataTimeSeriesDetailsShape = {
    uuid: UUIDSchema,
    publisher: MarketDataPublisherSchema.optional(),
    regions: z.array(MarketDataRegionSchema).optional(),
    points: z.array(MarketDataPointSchema),
};
const MarketDataTimeSeriesDetailsSchema = z.object(MarketDataTimeSeriesDetailsShape);
export const MarketDataTimeSeriesSchema = z.intersection(NewMarketDataTimeSeriesSchema, MarketDataTimeSeriesDetailsSchema);
const MarketDataTimeSeriesListShape = {
    time_series: z.array(MarketDataTimeSeriesSchema),
};
export const MarketDataTimeSeriesListSchema = z.object(MarketDataTimeSeriesListShape);
const MarketDataDataPointsListShape = {
    points: z.array(MarketDataPointSchema),
};
export const MarketDataDataPointsListSchema = z.object(MarketDataDataPointsListShape);
